import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/Header';
import { BottomNav } from '@/components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Plus, Copy, Users, Trophy, Settings, UserPlus, Crown, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Leagues = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [newLeagueName, setNewLeagueName] = useState('');
  const [newLeagueDescription, setNewLeagueDescription] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchLeagues = useCallback(async () => {
    // Don't fetch if no token - ProtectedRoute will redirect
    if (!token) {
      console.log('⚠️ [Leagues] No token available, skipping fetch');
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/leagues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLeagues(data);
      } else if (response.status === 401) {
        console.log('⚠️ [Leagues] 401 Unauthorized - token non valido');
        // ProtectedRoute will handle redirect
      }
    } catch (err) {
      toast.error('Errore nel caricamento delle leghe');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  // Handle auto-join from link
  useEffect(() => {
    const joinCode = searchParams.get('join');
    if (joinCode && !isLoading) {
      setInviteCode(joinCode);
      setShowJoinDialog(true);
      // Remove the query parameter
      searchParams.delete('join');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, isLoading]);

  const handleCreateLeague = async (e) => {
    e.preventDefault();
    if (!newLeagueName.trim()) {
      toast.error('Inserisci un nome per la lega');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newLeagueName,
          description: newLeagueDescription,
        }),
      });

      if (response.ok) {
        const newLeague = await response.json();
        setLeagues([...leagues, newLeague]);
        setShowCreateDialog(false);
        setNewLeagueName('');
        setNewLeagueDescription('');
        toast.success('Lega creata con successo!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nella creazione della lega');
      }
    } catch (err) {
      toast.error('Errore nella creazione della lega');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJoinLeague = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      toast.error('Inserisci il codice invito');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ invite_code: inviteCode }),
      });

      if (response.ok) {
        const joinedLeague = await response.json();
        setLeagues([...leagues.filter(l => l.id !== joinedLeague.id), joinedLeague]);
        setShowJoinDialog(false);
        setInviteCode('');
        toast.success('Ti sei unito alla lega!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel join della lega');
      }
    } catch (err) {
      toast.error('Errore nel join della lega');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Codice copiato!');
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header title="Le mie Leghe" />
      
      <main className="main-content p-4 max-w-5xl mx-auto">
        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="flex-1 rounded-full" data-testid="create-league-btn">
                <Plus className="w-4 h-4 mr-2" />
                Crea Lega
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crea una nuova Lega</DialogTitle>
                <DialogDescription>
                  Crea la tua lega e invita i tuoi compagni di classe
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateLeague}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="league-name">Nome della Lega</Label>
                    <Input
                      id="league-name"
                      placeholder="es. 5A Liceo Scientifico"
                      value={newLeagueName}
                      onChange={(e) => setNewLeagueName(e.target.value)}
                      data-testid="league-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="league-desc">Descrizione (opzionale)</Label>
                    <Input
                      id="league-desc"
                      placeholder="Descrivi la tua lega..."
                      value={newLeagueDescription}
                      onChange={(e) => setNewLeagueDescription(e.target.value)}
                      data-testid="league-desc-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={isSubmitting} data-testid="create-league-submit">
                    {isSubmitting ? 'Creazione...' : 'Crea Lega'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex-1 rounded-full" data-testid="join-league-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Unisciti
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Unisciti a una Lega</DialogTitle>
                <DialogDescription>
                  Inserisci il codice invito che hai ricevuto
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleJoinLeague}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-code">Codice Invito</Label>
                    <Input
                      id="invite-code"
                      placeholder="es. ABC12345"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                      className="uppercase"
                      data-testid="invite-code-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={isSubmitting} data-testid="join-league-submit">
                    {isSubmitting ? 'Unione...' : 'Unisciti'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Leagues List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : leagues.length > 0 ? (
          <div className="space-y-4 stagger-children">
            {leagues.map((league) => (
              <Card 
                key={league.id} 
                className="hover-lift cursor-pointer"
                onClick={() => navigate(`/leagues/${league.id}`)}
                data-testid={`league-item-${league.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {league.name}
                        {(league.owners?.includes(user?.id) || league.owner_id === user?.id) && (
                          <Crown className="w-4 h-4 text-yellow-500" />
                        )}
                      </CardTitle>
                      {league.description && (
                        <CardDescription>{league.description}</CardDescription>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {(league.owners?.includes(user?.id) || league.owner_id === user?.id) && (
                        <Badge variant="default">Owner</Badge>
                      )}
                      {league.admins?.includes(user?.id) && !(league.owners?.includes(user?.id) || league.owner_id === user?.id) && (
                        <Badge variant="secondary">
                          <Shield className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-2">
                        {league.members.slice(0, 4).map((member, i) => (
                          <Avatar key={member.id} className="w-8 h-8 border-2 border-background">
                            <AvatarImage src={member.avatar_url} />
                            <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                              {getInitials(member.name)}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                        {league.members.length > 4 && (
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium border-2 border-background">
                            +{league.members.length - 4}
                          </div>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {league.members.length} {league.members.length === 1 ? 'membro' : 'membri'}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyInviteCode(league.invite_code);
                      }}
                      className="text-xs"
                      data-testid={`copy-code-${league.id}`}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      {league.invite_code}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-dashed" data-testid="no-leagues">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                <Users className="w-8 h-8 text-muted-foreground" />
              </div>
              <h4 className="font-semibold mb-2">Nessuna lega</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Crea la tua prima lega o unisciti a una esistente usando un codice invito
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      <BottomNav />
    </div>
  );
};
